"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

export default function SignupPage() {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const marketingConsent = true;
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [signupCooldown, setSignupCooldown] = useState(0);
  const router = useRouter();

  useEffect(() => {
    if (signupCooldown <= 0) {
      return;
    }

    const interval = window.setInterval(() => {
      setSignupCooldown((current) => {
        if (current <= 1) {
          window.clearInterval(interval);
          return 0;
        }
        return current - 1;
      });
    }, 1000);

    return () => window.clearInterval(interval);
  }, [signupCooldown]);

  function toFrenchSignupError(raw: string): string {
    const normalized = raw.toLowerCase();

    if (normalized.includes("already registered") || normalized.includes("already exists")) {
      return "Un compte avec cet email existe déjà.";
    }

    if (normalized.includes("password")) {
      return "Mot de passe invalide. Utilise au moins 6 caractères.";
    }

    if (normalized.includes("invalid email") || normalized.includes("email address is invalid")) {
      return "Email invalide. Vérifie l'adresse saisie.";
    }

    if (normalized.includes("signup is disabled")) {
      return "Les inscriptions sont temporairement désactivées. Réessaie plus tard.";
    }

    if (normalized.includes("rate limit") || normalized.includes("too many requests")) {
      return "Trop de tentatives. Patiente une minute puis réessaie.";
    }

    if (normalized.includes("network") || normalized.includes("fetch")) {
      return "Problème de connexion réseau. Vérifie internet puis réessaie.";
    }

    return "Impossible de créer le compte pour le moment.";
  }

  function isRateLimitError(raw: string): boolean {
    const normalized = raw.toLowerCase();
    return normalized.includes("rate limit") || normalized.includes("too many requests");
  }

  function getOAuthCallbackOrigin(): string {
    const configuredOrigin = process.env.NEXT_PUBLIC_OAUTH_REDIRECT_ORIGIN?.trim();

    if (configuredOrigin) {
      return configuredOrigin.replace(/\/$/, "");
    }

    return window.location.origin;
  }

  async function handleGoogleSignup() {
    setGoogleLoading(true);
    setMessage("");

    if (typeof window !== "undefined") {
      localStorage.setItem("pending_marketing_consent", marketingConsent ? "true" : "false");
    }

    const params = new URLSearchParams(window.location.search);
    const rawNext = params.get("next") || "/dashboard";
    const nextPath = rawNext.startsWith("/") ? rawNext : "/dashboard";
    const callbackOrigin = getOAuthCallbackOrigin();
    const redirectTo = `${callbackOrigin}/auth/callback?next=${encodeURIComponent(nextPath)}&marketing_consent=true`;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo,
        queryParams: {
          access_type: "offline",
          prompt: "select_account",
        },
      },
    });

    if (error) {
      console.error("Google signup error:", error.message);
      setMessage("Connexion Google indisponible pour le moment.");
      setGoogleLoading(false);
    }
  }

  async function handleSignUp(e: React.FormEvent) {
    e.preventDefault();

    if (signupCooldown > 0) {
      return;
    }

    setLoading(true);
    setMessage("");

    const normalizedEmail = email.trim().toLowerCase();
    const trimmedFirstName = firstName.trim();

    if (!normalizedEmail || !trimmedFirstName) {
      setMessage("Renseigne ton prénom et ton email.");
      setLoading(false);
      return;
    }

    const { data, error } = await supabase.auth.signUp({
      email: normalizedEmail,
      password,
      options: {
        data: {
          first_name: trimmedFirstName,
          marketing_consent: true,
          marketing_consent_date: new Date().toISOString(),
        },
      },
    });

    if (error) {
      console.error("Signup error:", error.message);
      setMessage(toFrenchSignupError(error.message));
      if (isRateLimitError(error.message)) {
        setSignupCooldown(60);
      }
    } else if (data.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
      setMessage("Un compte avec cet email existe déjà.");
    } else if (!data.session) {
      setMessage("Compte créé. Vérifie ta boîte mail (et les indésirables) pour confirmer ton adresse.");
    } else {
      const params = new URLSearchParams(window.location.search);
      const rawNext = params.get("next") || "/dashboard";
      const nextPath = rawNext.startsWith("/") ? rawNext : "/dashboard";

      router.push(nextPath);
    }

    setLoading(false);
  }

  return (
    <main className="min-h-screen flex flex-col items-center justify-center px-4 pt-12 bg-linear-to-br from-violet-50 via-white to-violet-50/50">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="mb-4 flex justify-center">
            <img 
              src="/icons/icon-192.png" 
              alt="Noread logo" 
              className="h-16 w-16 rounded-3xl shadow-lg"
            />
          </div>
          <h1 className="text-5xl font-black text-slate-950 mb-2 tracking-tight">NOREAD</h1>
          <p className="text-slate-600 font-medium">Ton app de suivi de lecture</p>
          <p className="mt-2 text-sm text-slate-500">
            Simple, rapide, efficace.
          </p>
        </div>

        <form onSubmit={handleSignUp} className="w-full flex flex-col gap-4">
          <h2 className="text-xl font-bold text-center text-slate-900">Créer un compte</h2>

          <button
            type="button"
            onClick={handleGoogleSignup}
            disabled={loading || googleLoading}
            className="flex items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white py-3 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="text-base" aria-hidden="true">G</span>
            {googleLoading ? "Redirection vers Google..." : "Continuer avec Google"}
          </button>

          <div className="flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-slate-400">
            <span className="h-px flex-1 bg-slate-200" />
            <span>ou</span>
            <span className="h-px flex-1 bg-slate-200" />
          </div>

          <input
            type="text"
            placeholder="Prénom"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            className="border rounded-xl px-4 py-3"
            required
          />

          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="border rounded-xl px-4 py-3"
            required
          />

          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Mot de passe"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border rounded-xl px-4 py-3 pr-24"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((current) => !current)}
              className="absolute inset-y-0 right-3 my-auto h-fit text-xs font-medium text-slate-600 hover:text-slate-800"
            >
              {showPassword ? "Masquer" : "Afficher"}
            </button>
          </div>

          <button
            type="submit"
            disabled={loading || googleLoading || signupCooldown > 0}
            className="bg-indigo-600 text-white rounded-xl py-3"
          >
            {loading
              ? "Création..."
              : signupCooldown > 0
                ? `Réessaie dans ${signupCooldown}s`
                : "S'inscrire"}
          </button>

          <p className="text-xs text-center text-slate-500 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
            Après inscription, vérifie ton email de confirmation (pense aussi aux indésirables).
          </p>

          {message && <p className="text-sm text-center text-slate-600">{message}</p>}

          <p className="text-sm text-center">
            Déjà inscrit ?{" "}
            <Link href="/login" className="text-indigo-600">
              Se connecter
            </Link>
          </p>

          <p className="text-xs text-center leading-5 text-slate-500">
            En t&apos;inscrivant tu acceptes nos{" "}
            <Link href="/terms" className="text-indigo-600">Conditions d&apos;utilisation</Link>
            {" "}et notre{" "}
            <Link href="/privacy" className="text-indigo-600">Politique de confidentialité</Link>
            , ainsi que de recevoir les actualités de Noread par email.{" "}
            Tu peux te désabonner à tout moment.
          </p>
          
        </form>
      </div>
    </main>
  );
}
